import { useState, useEffect, useCallback } from 'react';
import { Sparkles, Save, Loader2, Check, BookOpen, Brain, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  {
    key: 'pitch',
    label: 'Pitch / Presentation',
    placeholder: "Ex: Le Mas est un restaurant-traiteur familial a Saint-Laurent-de-la-Salanque, specialise en cuisine catalane fait-maison. Laurent Sanmartin, le patron, met un point d'honneur a utiliser des produits locaux du Roussillon...",
    help: "Qui vous etes, votre histoire, ce qui vous differencie. L'IA l'utilisera pour se presenter.",
    category: 'general',
  },
  {
    key: 'specialites',
    label: 'Specialites & Points forts',
    placeholder: "Ex: Paella geante au feu de bois (jusqu'a 300 pers.), BBQ plancha en exterieur, plateaux de fruits de mer, cuisine catalane traditionnelle, menu vegan disponible, bar a tapas...",
    help: "Vos plats phares, types de cuisine, capacites speciales. L'IA les mettra en avant.",
    category: 'general',
  },
  {
    key: 'references',
    label: 'References clients & Evenements',
    placeholder: "Ex: Partenaire officiel USAP et Dragons Catalans. Traiteur du gala des Toques Blanches 2025. Plus de 200 mariages realises. Repas pour le CE de la SNCF Perpignan (320 pers.)...",
    help: "Vos realisations passees. L'IA s'en servira comme preuves sociales dans les emails.",
    category: 'general',
  },
  {
    key: 'ton',
    label: 'Ton & Style de communication',
    placeholder: "Ex: Je veux un ton chaleureux mais professionnel. Pas trop commercial, plutot comme si je parlais a un ami. Toujours vouvoyer. Signer 'Laurent' pas 'M. Sanmartin'. Ne jamais utiliser 'n'hesitez pas'...",
    help: "Comment vous voulez que l'IA communique en votre nom. Soyez precis !",
    category: 'general',
  },
  {
    key: 'infos_pratiques',
    label: 'Infos pratiques',
    placeholder: "Ex: Capacite 150 pers. en salle, 300 en exterieur. Parking 50 places. Acces PMR. On se deplace dans tout le 66, 11 et 34. Livraison possible. Materiel complet (tables, nappes, vaisselle)...",
    help: "Capacites, zones de livraison, equipements. L'IA les citera quand c'est pertinent.",
    category: 'general',
  },
  {
    key: 'tarifs_traiteur',
    label: 'Tarifs & Formules Traiteur',
    placeholder: "Ex: Formule buffet a partir de 25€/pers. Menu assis 3 plats: 35€/pers. Paella geante: 18€/pers (min 50). Dessert buffet: +8€/pers. Boissons forfait: 15€/pers. Supplement deplace > 50km...",
    help: "Vos tarifs, formules, conditions. L'IA s'en servira pour les propositions et devis.",
    category: 'traiteur',
  },
  {
    key: 'conditions_traiteur',
    label: 'Conditions & Logistique Traiteur',
    placeholder: "Ex: Acompte 30% a la commande. Nombre definitif J-7. Annulation sans frais J-15. On fournit tout le materiel. Personnel de service en option (25€/h). Menu degustation offert pour les mariages > 100 pers...",
    help: "Conditions de vente, logistique, avantages. Pour les propositions et negociations.",
    category: 'traiteur',
  },
  {
    key: 'objections_reponses',
    label: 'Objections & Bonnes reponses',
    placeholder: "Ex: 'C'est trop cher' → 'Nos tarifs incluent tout le materiel, le personnel, le transport, et des produits 100% frais et locaux. Comparez avec le cout de tout organiser separement...'\n'Vous etes trop loin' → 'Nous nous deplacons dans tout le 66, 11 et 34 sans supplement...'",
    help: "Les objections que vous recevez souvent et VOS meilleures reponses. L'IA les utilisera.",
    category: 'commercial',
  },
  {
    key: 'emails_modeles',
    label: 'Exemples d\'emails qui marchent',
    placeholder: "Collez ici vos meilleurs emails — ceux qui ont eu des reponses positives. L'IA analysera votre style et le reproduira.",
    help: "Vos emails gagnants. L'IA apprendra votre style qui fonctionne.",
    category: 'commercial',
  },
  {
    key: 'prompts_perso',
    label: 'Prompts & Instructions pour Claude',
    placeholder: "Collez ici vos textes prepares avec Claude, vos instructions specifiques pour l'IA, des exemples, des regles a suivre, etc.",
    help: "Champ libre — tout ce que vous voulez que l'IA sache et utilise. C'est la section prioritaire.",
    category: 'avance',
  },
];

const CATEGORIES = [
  { key: 'general', label: 'Mon entreprise', color: '#C9A84C' },
  { key: 'traiteur', label: 'Traiteur & Tarifs', color: '#E07A5F' },
  { key: 'commercial', label: 'Commercial', color: '#5B8FE0' },
  { key: 'avance', label: 'IA Avance', color: '#8B5CF6' },
];

export default function IAKnowledgePage() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [expandedSection, setExpandedSection] = useState('pitch');

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/crm/ia-knowledge`);
      const d = await res.json();
      setData(d);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/crm/ia-knowledge`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (res.ok) {
        toast.success('Base de connaissances sauvegardee !');
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      }
    } catch {
      toast.error('Erreur sauvegarde');
    }
    setSaving(false);
  };

  const filledCount = SECTIONS.filter(s => data[s.key] && data[s.key].trim().length > 10).length;
  const totalChars = SECTIONS.reduce((sum, s) => sum + (data[s.key] || '').length, 0);

  if (loading) return (
    <div className="flex justify-center py-20">
      <Loader2 size={20} className="animate-spin text-[rgb(var(--lm-gold))]" />
    </div>
  );

  return (
    <div className="space-y-5" data-testid="ia-knowledge-page">
      {/* Header */}
      <div>
        <p className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-0.5">Intelligence Artificielle</p>
        <h1 className="text-base font-semibold text-[rgb(var(--lm-text))]">Base de connaissances IA</h1>
      </div>

      {/* Progress Card */}
      <div className="lm-card p-4 border border-[rgb(var(--lm-gold))]/20">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[rgb(var(--lm-gold))]/10 flex items-center justify-center shrink-0">
            <Brain size={18} className="text-[rgb(var(--lm-gold))]" />
          </div>
          <div className="flex-1">
            <p className="text-xs text-[rgb(var(--lm-text-secondary))] leading-relaxed">
              Plus vous renseignez d'informations, plus l'IA sera precise et personnalisee
              pour vos emails, propositions, relances et scripts d'appel.
            </p>
            <div className="flex items-center gap-3 mt-3">
              <div className="flex gap-0.5 flex-1">
                {SECTIONS.map((s, i) => (
                  <div key={i} className={`flex-1 h-1.5 rounded-full transition-colors ${
                    data[s.key] && data[s.key].trim().length > 10 ? 'bg-[rgb(var(--lm-gold))]' : 'bg-[rgb(var(--lm-border))]/30'
                  }`} />
                ))}
              </div>
              <span className="text-[10px] text-[rgb(var(--lm-gold))] font-medium shrink-0">{filledCount}/{SECTIONS.length}</span>
            </div>
            {totalChars > 0 && (
              <p className="text-[9px] text-[rgb(var(--lm-text-muted))] mt-1">{totalChars.toLocaleString()} caracteres au total</p>
            )}
          </div>
        </div>
      </div>

      {/* Sections by Category */}
      {CATEGORIES.map(cat => {
        const catSections = SECTIONS.filter(s => s.category === cat.key);
        return (
          <div key={cat.key}>
            <p className="text-[9px] tracking-[0.15em] uppercase font-medium mb-2" style={{ color: cat.color }}>{cat.label}</p>
            <div className="space-y-1.5">
              {catSections.map(section => {
                const isExpanded = expandedSection === section.key;
                const hasContent = data[section.key] && data[section.key].trim().length > 10;
                return (
                  <div key={section.key} className={`lm-card overflow-hidden transition-all ${
                    isExpanded ? 'ring-1 ring-[rgb(var(--lm-gold))]/30' : ''
                  }`}>
                    <button
                      onClick={() => setExpandedSection(isExpanded ? '' : section.key)}
                      className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[rgb(var(--lm-bg))]/50 transition-colors"
                      data-testid={`kb-section-${section.key}`}
                    >
                      <div className="flex items-center gap-2.5">
                        {hasContent ? (
                          <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: cat.color + '20' }}>
                            <Check size={10} style={{ color: cat.color }} />
                          </div>
                        ) : (
                          <div className="w-5 h-5 rounded-full border border-[rgb(var(--lm-border))]/50" />
                        )}
                        <span className={`text-xs font-medium ${isExpanded ? 'text-[rgb(var(--lm-text))]' : 'text-[rgb(var(--lm-text-secondary))]'}`}>
                          {section.label}
                        </span>
                        {hasContent && (
                          <span className="text-[8px] text-[rgb(var(--lm-text-muted))]">{(data[section.key] || '').length} car.</span>
                        )}
                      </div>
                      {isExpanded ? <ChevronUp size={12} className="text-[rgb(var(--lm-text-muted))]" /> : <ChevronDown size={12} className="text-[rgb(var(--lm-text-muted))]" />}
                    </button>
                    {isExpanded && (
                      <div className="px-4 pb-4 space-y-2 animate-fade-in">
                        <p className="text-[10px] text-[rgb(var(--lm-text-muted))] leading-relaxed">{section.help}</p>
                        <textarea
                          value={data[section.key] || ''}
                          onChange={e => setData(prev => ({ ...prev, [section.key]: e.target.value }))}
                          placeholder={section.placeholder}
                          rows={7}
                          className="w-full bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))]/30 rounded-lg px-3 py-2.5 text-sm text-[rgb(var(--lm-text))] placeholder:text-[rgb(var(--lm-text-muted))]/40 resize-none focus:border-[rgb(var(--lm-gold))]/50 focus:ring-1 focus:ring-[rgb(var(--lm-gold))]/20 outline-none leading-relaxed"
                          data-testid={`kb-input-${section.key}`}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Save Button */}
      <Button onClick={save} disabled={saving} className="w-full h-10" style={{ background: 'rgb(var(--lm-gold))', color: '#fff' }} data-testid="kb-save-btn">
        {saving ? <><Loader2 size={14} className="animate-spin mr-2" /> Sauvegarde...</>
          : saved ? <><Check size={14} className="mr-2" /> Sauvegarde !</>
          : <><Save size={14} className="mr-2" /> Sauvegarder</>}
      </Button>

      {/* How it works */}
      <div className="lm-card p-3 border border-[rgb(var(--lm-border))]/20">
        <div className="flex items-center gap-2 mb-2">
          <BookOpen size={11} className="text-[rgb(var(--lm-text-muted))]" />
          <span className="text-[9px] font-medium text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">Ou c'est utilise ?</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px] text-[rgb(var(--lm-text-secondary))]">
          <div className="flex items-center gap-1.5"><Sparkles size={10} className="text-[#E07A5F]" /> Emails prospection</div>
          <div className="flex items-center gap-1.5"><Sparkles size={10} className="text-[#C9A84C]" /> Propositions traiteur</div>
          <div className="flex items-center gap-1.5"><Sparkles size={10} className="text-[#5B8FE0]" /> Scripts d'appel</div>
          <div className="flex items-center gap-1.5"><Sparkles size={10} className="text-[#8B5CF6]" /> Objections IA</div>
        </div>
      </div>
    </div>
  );
}
